import { useFileOrganizer } from "@via/hooks/useFileOrganizer.tsx";
import { type IImportedFile } from "@via/pages/data/file-organizer/file-organizer.tsx";
import {
  getFileNameWithoutPath,
  getMergedDirectoryPath,
  getNameWithPath,
  getSampleName,
  groupPairedFilesByPattern,
  IPairedItem,
} from "@via/pages/data/file-organizer/file-patterns/helpers.tsx";
import { type JSX, useEffect, useState } from "react";
import { classNames } from "@via/utils/helpers.tsx";
import { AutoMergePatternInput } from "@via/pages/data/file-organizer/file-patterns/auto-merge-pattern-input.tsx";
import { Sample } from "@via/context/file-organizer-context.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { DataSourceProvider } from "common";

export default function PairedList({
  fileList,
  globalBucketName,
  toBeRemovedFileIds,
  provider,
}: {
  fileList: IImportedFile[];
  globalBucketName: string;
  toBeRemovedFileIds: string[];
  provider: DataSourceProvider;
}): JSX.Element {
  const { setNotification, NOTIFICATION } = useNotifications();
  const { addNewSamples, resetSampleList, resetTrigger } = useFileOrganizer();

  const [inputLeft, setInputLeft] = useState<string>("_R1");
  const [inputRight, setInputRight] = useState<string>("_R2");

  const [leftList, setLeftList] = useState<IImportedFile[]>([]);
  const [rightList, setRightList] = useState<IImportedFile[]>([]);

  const [pairedList, setPairedList] = useState<IPairedItem[]>([]);

  useEffect(() => {
    setPairedList(prevPairedList =>
      prevPairedList.map(pairedItem => {
        if (
          toBeRemovedFileIds.includes(pairedItem.original?.id || "") ||
          toBeRemovedFileIds.includes(pairedItem.pair?.id || "")
        ) {
          return {
            ...pairedItem,
            isChecked: false,
            isSampled: false,
          };
        } else {
          return pairedItem;
        }
      })
    );
  }, [toBeRemovedFileIds]);

  useEffect(() => {
    const newLeftList = fileList.filter(item => item.name.includes(inputLeft));
    setLeftList(newLeftList);
  }, [inputLeft]);

  useEffect(() => {
    const newRightList = fileList.filter(item =>
      item.name.includes(inputRight)
    );
    setRightList(newRightList);
  }, [inputRight]);

  useEffect(() => {
    const usedRightItems: any = [];

    const newPairedList: IPairedItem[] = leftList.map(leftItem => {
      const pair = rightList.find(rightItem => {
        const leftPrefix = leftItem.name.split(inputLeft)[0];
        const rightPrefix = rightItem.name.split(inputRight)[0];
        const isRightItemAlreadyUsed = usedRightItems.includes(rightItem.name);

        return (
          !isRightItemAlreadyUsed &&
          rightPrefix === leftPrefix &&
          leftItem.name !== rightItem.name
        );
      });

      if (pair) {
        usedRightItems.push(pair.name);
      }

      return { original: leftItem, pair, isChecked: false };
    });

    // Add unmatched items from rightList
    rightList.forEach(rightItem => {
      const isUnmatched = !newPairedList.some(
        pair => pair.pair && pair.pair.name === rightItem.name
      );
      if (isUnmatched) {
        newPairedList.push({
          original: undefined,
          pair: rightItem,
          isChecked: false,
        });
      }
    });

    setPairedList(newPairedList);
  }, [leftList, rightList, inputLeft, inputRight]);

  const handleInputChange = (input: string, isLeft: boolean): void => {
    if (isLeft) {
      setInputLeft(input);
    } else {
      setInputRight(input);
    }
  };

  const handlePairedListClick = (clickedItem: IPairedItem): void => {
    setPairedList(prevList => {
      const newList = prevList.map(item =>
        item === clickedItem ? { ...item, isChecked: !item.isChecked } : item
      );
      return newList;
    });
  };

  const getIds = (pair: IPairedItem): string => {
    const firstItem = pair.original?.id || "";
    const secondItem = pair.pair?.id || "";

    return [firstItem, secondItem].filter(Boolean).join(",");
  };

  const getIdsReordered = (pairs: IPairedItem[]): string => {
    const originalIds: string[] = [];
    const pairIds: string[] = [];

    pairs.forEach(pair => {
      if (pair.original?.id) originalIds.push(pair.original.id);
      if (pair.pair?.id) pairIds.push(pair.pair.id);
    });

    // Combine all R1 first, then R2
    return [...originalIds, ...pairIds].join(",");
  };

  const getFilesUsed = (pair: IPairedItem): string => {
    const firstItem = getFileNameWithoutPath(pair.original?.name || "");
    const secondItem = getFileNameWithoutPath(pair.pair?.name || "");

    return [firstItem, secondItem].filter(Boolean).join(",");
  };

  const getDirectories = (pair: IPairedItem): string => {
    const firstItem = pair.original?.name || "";
    const secondItem = pair.pair?.name || "";

    return [firstItem, secondItem].filter(Boolean).join(",");
  };

  const handleAutoMergeClick = (autoMergePattern: string): void => {
    const unSampledPairs = pairedList.filter(p => !p.isSampled);

    try {
      const grouped = groupPairedFilesByPattern(
        unSampledPairs,
        autoMergePattern
      );

      const toBeAddedSamples: Sample[] = [];

      Object.entries(grouped).forEach(([groupKey, items]) => {
        const mergedSample = {
          id: "",
          name: groupKey,
          filesUsed: "",
          directory: "",
          file1: "",
          file2: "",
          "Provider IDs": "",
        };

        items.forEach((item, index) => {
          item.isSampled = true;

          const ids = getIds(item);
          const files = getFilesUsed(item);
          const dirs = getDirectories(item);

          if (index > 0) {
            mergedSample.id += ",";
            mergedSample.filesUsed += " | ";
            mergedSample.directory += ",";
            mergedSample.file1 += ",";
            mergedSample.file2 += ",";
            mergedSample["Provider IDs"] += ",";
          }

          mergedSample.id += ids;
          mergedSample.filesUsed += files;
          mergedSample.directory += dirs;
          mergedSample.file1 += getNameWithPath(
            item.original?.path || "",
            item.original?.displayName || "",
            item.original?.name || "",
            provider
          );
          mergedSample.file2 += getNameWithPath(
            item.pair?.path || "",
            item.pair?.displayName || "",
            item.pair?.name || "",
            provider
          );
          mergedSample["Provider IDs"] +=
            provider === DataSourceProvider.BaseSpace ? getIds(item) : "";
        });

        if (provider === DataSourceProvider.BaseSpace) {
          mergedSample["Provider IDs"] = getIdsReordered(items);
        }

        mergedSample.directory = getMergedDirectoryPath(
          mergedSample.directory,
          globalBucketName
        );

        toBeAddedSamples.push(mergedSample);
      });

      addNewSamples(toBeAddedSamples);
    } catch (error) {
      setNotification({
        id: "paired-auto-merge-failed",
        message: "Auto Merge Failed",
        details: "Invalid Pattern",
        variant: NOTIFICATION.ERROR,
      });
    }
  };

  const handleResetClick = (): void => {
    resetSampleList();

    pairedList.forEach(file => {
      file.isSampled = false;
      file.isChecked = false;
    });
  };

  // Watch for reset trigger from context (e.g., when vmetaProjectId changes)
  useEffect(() => {
    if (resetTrigger > 0) {
      handleResetClick();
    }
  }, [resetTrigger]);

  const handleMergeSelectedFilesClick = (): void => {
    const mergedSample = {
      id: "",
      name: "",
      filesUsed: "",
      directory: "",
      file1: "",
      file2: "",
      "Provider IDs": "",
    };

    const selectedFiles = pairedList.filter(
      file => file.isChecked && !file.isSampled
    );
    if (selectedFiles && selectedFiles.length > 0) {
      mergedSample.name = getSampleName(
        getFileNameWithoutPath(
          selectedFiles[0].original?.name || selectedFiles[0].pair?.name || ""
        ),
        inputLeft || inputRight || "."
      );

      selectedFiles.forEach((file, index) => {
        file.isSampled = true;

        if (index > 0) {
          mergedSample.id += ",";
          mergedSample.filesUsed += " | ";
          mergedSample.directory += ",";
          mergedSample.file1 += ",";
          mergedSample.file2 += ",";
          mergedSample["Provider IDs"] += ",";
        }

        mergedSample.id += getIds(file);
        mergedSample.filesUsed += getFilesUsed(file);
        mergedSample.directory += getDirectories(file);
        mergedSample.file1 += getNameWithPath(
          file.original?.path || "",
          file.original?.displayName || "",
          file.original?.name || "",
          provider
        );
        mergedSample.file2 += getNameWithPath(
          file.pair?.path || "",
          file.pair?.displayName || "",
          file.pair?.name || "",
          provider
        );
      });

      if (provider === DataSourceProvider.BaseSpace) {
        mergedSample["Provider IDs"] = getIdsReordered(selectedFiles);
      }

      mergedSample.directory = getMergedDirectoryPath(
        mergedSample.directory,
        globalBucketName
      );

      addNewSamples([mergedSample]);
    }
  };

  const handleAddSelectedFilesClick = (): void => {
    const sampleList = pairedList
      .filter(file => file.isChecked && !file.isSampled)
      .map(file => {
        file.isSampled = true;

        return {
          id: getIds(file),
          name: getSampleName(
            getFileNameWithoutPath(
              file.original?.name || file.pair?.name || ""
            ),
            inputLeft || inputRight || "."
          ),
          filesUsed: getFilesUsed(file),
          directory: getMergedDirectoryPath(
            getDirectories(file),
            globalBucketName
          ),
          file1: getNameWithPath(
            file.original?.path || "",
            file.original?.displayName || "",
            file.original?.name || "",
            provider
          ),
          file2: getNameWithPath(
            file.pair?.path || "",
            file.pair?.displayName || "",
            file.pair?.name || "",
            provider
          ),
          "Provider IDs":
            provider === DataSourceProvider.BaseSpace ? getIds(file) : "",
        };
      });

    addNewSamples(sampleList);
  };

  const handleAddAllFilesClick = (): void => {
    const sampleList = pairedList
      .filter(
        file =>
          !file.isSampled &&
          file.original !== undefined &&
          file.pair !== undefined
      )
      .map(file => {
        file.isSampled = true;

        return {
          id: getIds(file),
          name: getSampleName(
            getFileNameWithoutPath(
              file.original?.name || file.pair?.name || ""
            ),
            inputLeft || inputRight || "."
          ),
          filesUsed: getFilesUsed(file),
          directory: getMergedDirectoryPath(
            getDirectories(file),
            globalBucketName
          ),
          file1: getNameWithPath(
            file.original?.path || "",
            file.original?.displayName || "",
            file.original?.name || "",
            provider
          ),
          file2: getNameWithPath(
            file.pair?.path || "",
            file.pair?.displayName || "",
            file.pair?.name || "",
            provider
          ),
          "Provider IDs":
            provider === DataSourceProvider.BaseSpace ? getIds(file) : "",
        };
      });

    addNewSamples(sampleList);
  };

  const buttonDetails = [
    {
      label: "Reset",
      onClick: handleResetClick,
      testId: "pairedListResetButton",
    },
    {
      label: "Merge Selected Files",
      onClick: handleMergeSelectedFilesClick,
      testId: "pairedListMergeButton",
    },
    {
      label: "Add Selected Files",
      onClick: handleAddSelectedFilesClick,
      testId: "pairedListAddSelectedButton",
    },
    {
      label: "Add All Files",
      onClick: handleAddAllFilesClick,
      testId: "pairedListAddAllButton",
    },
  ];

  return (
    <div className="mt-8">
      <div className="flex gap-4">
        <div className="flex flex-1 items-center gap-4">
          <label className="block whitespace-nowrap text-sm font-medium leading-6 text-gray-900">
            R1 Pattern
          </label>

          <input
            data-testid="r1PatternInput"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            type="text"
            value={inputLeft}
            onChange={e => {
              handleInputChange(e.target.value, true);
            }}
          />
        </div>

        <div className="flex flex-1 items-center gap-4">
          <label className="block whitespace-nowrap text-sm font-medium leading-6 text-gray-900">
            R2 Pattern
          </label>

          <input
            data-testid="r2PatternInput"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            type="text"
            value={inputRight}
            onChange={e => {
              handleInputChange(e.target.value, false);
            }}
          />
        </div>
      </div>

      <div
        data-testid="pairedListDiv"
        className="mt-4 flex h-60 flex-col overflow-auto rounded-md border border-gray-200">
        {pairedList.map((item, index) =>
          item.isSampled ? null : (
            <div
              className={classNames(
                "flex cursor-pointer gap-3 px-2 py-2 hover:bg-gray-50 text-sm",
                item.isChecked ? "bg-indigo-100 hover:bg-indigo-200" : ""
              )}
              key={index}
              onClick={() => {
                handlePairedListClick(item);
              }}>
              <div className="flex-1">
                {getFileNameWithoutPath(item.original?.name || "")}
              </div>
              <div className="flex-1">
                {getFileNameWithoutPath(item.pair?.name || "")}
              </div>
            </div>
          )
        )}
      </div>

      <AutoMergePatternInput onAutoMergeClick={handleAutoMergeClick} />

      <div className="mt-5 flex justify-end gap-2">
        {buttonDetails.map((button, index) => (
          <button
            data-testid={button.testId}
            key={index}
            onClick={button.onClick}
            className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
            {button.label}
          </button>
        ))}
      </div>
    </div>
  );
}
